import React from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  ChartAxis,
  ChartMarker,
  ChartPane,
  ChartRow,
  ChartSeries,
  ChartSpec,
  ChartStyle,
} from "./types";
import { chartTimeRange, formatChartTime } from "./timeRange";

const DEFAULT_COLORS = ["#60A5FA", "#A78BFA", "#22C55E", "#F59E0B", "#F472B6", "#22D3EE"];
const TONE_BACKGROUNDS = {
  primary: "#0b1220",
  indicator: "#0d1525",
  volume: "#0a1420",
  comparison: "#0d1422",
  neutral: "#0b1220",
};

type PlotRow = ChartRow & { __chartX: number; __chartIndex: number };

export function chartCoordinate(spec: ChartSpec, value: unknown, index: number): number {
  if (spec.xAxis.scaleMode === "ordinal" || spec.xAxis.scaleMode === "session") return index;
  if (spec.xAxis.type === "number") return Number(value);
  if (spec.xAxis.type === "time") return new Date(String(value)).getTime();
  return index;
}

function plottedRows(spec: ChartSpec): PlotRow[] {
  return spec.data.map((row, index) => ({
    ...row,
    __chartX: chartCoordinate(spec, row[spec.xAxis.key], index),
    __chartIndex: index,
  }));
}

function formatNumber(value: unknown, format: ChartAxis["format"]): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  if (format === "currency") {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(number);
  }
  if (format === "percent") return `${number.toFixed(2)}%`;
  if (format === "compact") {
    return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(number);
  }
  if (format === "integer") return Math.round(number).toLocaleString();
  return number.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatX(spec: ChartSpec, row: PlotRow | undefined, range: ReturnType<typeof chartTimeRange>, detail = false): string {
  if (!row) return "";
  const value = row[spec.xAxis.key];
  if (spec.xAxis.type !== "time") return String(value ?? "");
  return formatChartTime(value, range, spec.xAxis.timezone, detail);
}

function dashArray(dash: ChartStyle["dash"]): string | undefined {
  if (dash === "dashed") return "7 5";
  if (dash === "dotted") return "2 4";
  return undefined;
}

function lineType(interpolation: ChartSeries["interpolation"]): "linear" | "stepAfter" | "monotone" {
  if (interpolation === "step") return "stepAfter";
  if (interpolation === "monotone") return "monotone";
  return "linear";
}

function seriesValue(row: ChartRow | undefined, series: ChartSeries): number | null {
  if (!row) return null;
  const key = series.mark === "candlestick" ? series.fieldMapping?.close : series.dataKey;
  const rawValue = key ? row[key] : undefined;
  if (rawValue === null || rawValue === undefined || rawValue === "") return null;
  const value = Number(rawValue);
  return Number.isFinite(value) ? value : null;
}

function barColor(series: ChartSeries, row: ChartRow, previousRow?: ChartRow): string {
  const mapping = series.barColors;
  if (!mapping) return series.style?.color || series.style?.fill || "#38BDF8";
  if (mapping.mode === "field") {
    const value = String(mapping.colorKey ? row[mapping.colorKey] ?? "" : "").toLowerCase();
    if (["up", "positive", "green", "true", "1"].includes(value)) return mapping.positiveColor;
    if (["down", "negative", "red", "false", "-1"].includes(value)) return mapping.negativeColor;
    return mapping.neutralColor || "#64748B";
  }
  const close = Number(mapping.closeKey ? row[mapping.closeKey] : Number.NaN);
  const comparison = Number(
    mapping.openKey
      ? row[mapping.openKey]
      : mapping.comparisonKey
        ? previousRow?.[mapping.comparisonKey]
        : previousRow?.[mapping.closeKey || ""],
  );
  if (!Number.isFinite(close) || !Number.isFinite(comparison)) return mapping.neutralColor || "#64748B";
  if (close > comparison) return mapping.positiveColor;
  if (close < comparison) return mapping.negativeColor;
  return mapping.neutralColor || "#64748B";
}

function CandlestickShape(props: any): React.ReactElement | null {
  const { x, y, width, height, payload, series } = props;
  const mapping = series.fieldMapping;
  const open = Number(payload[mapping.open]);
  const high = Number(payload[mapping.high]);
  const low = Number(payload[mapping.low]);
  const close = Number(payload[mapping.close]);
  if (![x, y, width, height, open, high, low, close].every(Number.isFinite)) return null;
  const range = Math.max(high - low, Number.EPSILON);
  const openY = y + ((high - open) / range) * height;
  const closeY = y + ((high - close) / range) * height;
  const color = close >= open ? series.style?.positiveColor || "#22C55E" : series.style?.negativeColor || "#EF4444";
  const center = x + width / 2;
  const bodyWidth = Math.max(3, width * 0.62);
  return (
    <g data-series-mark="candlestick" data-series-id={series.id} data-semantic-element-id={series.semanticElementId}>
      <line x1={center} x2={center} y1={y} y2={y + height} stroke={color} strokeWidth={1.5} />
      <rect x={center - bodyWidth / 2} y={Math.min(openY, closeY)} width={bodyWidth} height={Math.max(Math.abs(closeY - openY), 2)} rx={1} fill={color} />
    </g>
  );
}

function MarkerLabel({ cx, cy, marker }: any): React.ReactElement {
  const labelY = cy + (marker.placement === "above" ? -18 : marker.placement === "below" ? 24 : -12);
  const width = Math.max(42, String(marker.label).length * 6.5 + 14);
  return (
    <g data-chart-marker={marker.id} data-semantic-element-id={marker.semanticElementId}>
      <circle cx={cx} cy={cy} r={5} fill={marker.color} stroke="#08111F" strokeWidth={2} />
      <rect x={cx + 7} y={labelY - 13} width={width} height={18} rx={5} fill="#08111F" stroke={marker.color} data-testid={`chart-label-background-${marker.id}`} />
      <text x={cx + 14} y={labelY} fill="#F8FAFC" fontSize={11} fontWeight={700}>{marker.label}</text>
    </g>
  );
}

function ScatterPoint({ cx, cy, color, seriesId }: any): React.ReactElement | null {
  if (![cx, cy].every(Number.isFinite)) return null;
  return <circle cx={cx} cy={cy} r={4} fill={color} stroke="#08111F" strokeWidth={1.5} data-series-point={seriesId} />;
}

function ReferenceLabel({ viewBox, value, color, id, semanticElementId, position = "end", offset = 0 }: any): React.ReactElement | null {
  const x = Number(viewBox?.x ?? viewBox?.x1);
  const y = Number(viewBox?.y ?? viewBox?.y1) + offset;
  const width = Number(viewBox?.width ?? (Number(viewBox?.x2) - Number(viewBox?.x1)));
  if (![x, y, width].every(Number.isFinite)) return null;
  const label = String(value);
  const labelWidth = Math.max(42, label.length * 6.5 + 14);
  const rectX = position === "start" ? x + 6 : x + width - labelWidth - 6;
  return (
    <g data-chart-reference-label={id} data-semantic-element-id={semanticElementId}>
      <rect x={rectX} y={y - 15} width={labelWidth} height={18} rx={5} fill="#08111F" stroke={color} />
      <text x={rectX + labelWidth / 2} y={y - 2} fill="#F8FAFC" fontSize={11} fontWeight={700} textAnchor="middle">{label}</text>
    </g>
  );
}

function markerValue(spec: ChartSpec, marker: ChartMarker): number | null {
  const row = spec.data.find((candidate) => candidate[spec.xAxis.key] === marker.x);
  const series = spec.series.find((candidate) => candidate.id === marker.seriesId);
  return row && series ? seriesValue(row, series) : null;
}

function renderSeries(series: ChartSeries, index: number, rows: PlotRow[]): React.ReactNode {
  const color = series.style?.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
  const common = {
    yAxisId: series.axisId,
    name: series.label,
    stroke: color,
    fill: series.style?.fill || color,
    opacity: series.style?.opacity ?? 1,
    isAnimationActive: false,
    "data-series-id": series.id,
    "data-semantic-element-id": series.semanticElementId,
  };
  if (series.mark === "candlestick") {
    const mapping = series.fieldMapping!;
    return <Bar key={series.id} {...common} dataKey={(row: Record<string, unknown>) => [Number(row[mapping.low]), Number(row[mapping.high])]} shape={(props: any) => <CandlestickShape {...props} series={series} />} maxBarSize={22} />;
  }
  if (series.mark === "bar") {
    return (
      <Bar key={series.id} {...common} dataKey={series.dataKey!} maxBarSize={24} radius={[2, 2, 0, 0]} data-series-mark="bar">
        {rows.map((row, rowIndex) => <Cell key={`${series.id}-${row.__chartIndex}`} fill={barColor(series, row, rows[rowIndex - 1])} />)}
      </Bar>
    );
  }
  if (series.mark === "area") return <Area key={series.id} {...common} dataKey={series.dataKey!} type={lineType(series.interpolation)} strokeWidth={series.style?.lineWidth || 2} fillOpacity={series.style?.opacity ?? 0.18} data-series-mark="area" />;
  if (series.mark === "scatter") {
    return <Line key={series.id} {...common} className="chart-series--scatter" dataKey={series.dataKey!} type="linear" connectNulls={false} stroke="none" dot={(props: any) => <ScatterPoint {...props} color={color} seriesId={series.id} />} activeDot={{ r: 5 }} data-series-mark="scatter" />;
  }
  return <Line key={series.id} {...common} dataKey={series.dataKey!} type={lineType(series.interpolation)} connectNulls={false} strokeWidth={series.style?.lineWidth || 2} strokeDasharray={dashArray(series.style?.dash || "solid")} dot={false} activeDot={{ r: 4 }} data-series-mark="line" />;
}

type TimeSeriesPlotProps = {
  spec: ChartSpec;
  pane: ChartPane;
  height: number;
  showXAxis: boolean;
  visibleRange: [number, number];
  activeIndex: number;
  hiddenSeries: Set<string>;
  scaleMode: 'linear' | 'log' | 'percentage' | 'indexed';
  onHover: (index: number) => void;
};

export default function TimeSeriesPlot({ spec, pane, height, showXAxis, visibleRange, activeIndex, hiddenSeries, scaleMode, onHover }: TimeSeriesPlotProps): React.ReactElement {
  const allRows = plottedRows(spec);
  const rows = allRows.slice(visibleRange[0], visibleRange[1] + 1);
  const axes = spec.axes.filter((axis) => pane.axisIds.includes(axis.id));
  const series = spec.series.filter((item) => item.paneId === pane.id && !hiddenSeries.has(item.id));
  const references = (spec.referenceLines || []).filter((line) => line.paneId === pane.id);
  const bands = (spec.referenceBands || []).filter((band) => band.paneId === pane.id);
  const windows = (spec.eventWindows || []).filter((window) => window.paneId === pane.id);
  const markers = (spec.markers || []).filter((marker) => marker.paneId === pane.id);
  const activeRow = spec.data[activeIndex];
  const timeRange = chartTimeRange(spec);
  const xByValue = new Map(allRows.map((row) => [row[spec.xAxis.key], row.__chartX]));
  const background = pane.appearance?.backgroundColor || TONE_BACKGROUNDS[pane.appearance?.tone || "neutral"];

  return (
    <div className="unified-chart__pane" data-pane-id={pane.id} data-pane-tone={pane.appearance?.tone || "neutral"} style={{ height, backgroundColor: background }}>
      <div className="unified-chart__pane-readout" data-testid={`chart-readout-${pane.id}`}>
        <strong>{pane.label || pane.id}</strong>
        <span>{formatX(spec, allRows[activeIndex], timeRange, true)}</span>
        {series.flatMap((item) => {
          if (item.mark === "candlestick" && item.fieldMapping) {
            return (["open", "high", "low", "close"] as const).map((field) => (
              <span key={`${item.id}-${field}`}><b>{field[0].toUpperCase()}</b> {String(activeRow?.[item.fieldMapping![field]] ?? "")}</span>
            ));
          }
          const value = seriesValue(activeRow, item);
          return value === null ? [] : [<span key={item.id} style={{ color: item.style?.color }}><b>{item.label}</b> {formatNumber(value, axes.find((axis) => axis.id === item.axisId)?.format || "number")}</span>];
        })}
        {(spec.hoverFields || []).filter((field) => field.paneId === pane.id && !series.some((item) => item.dataKey === field.dataKey)).map((field) => (
          <span key={field.id} style={{ color: field.color }}><b>{field.label}</b> {String(activeRow?.[field.dataKey] ?? "")}</span>
        ))}
      </div>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 38, right: 8, bottom: showXAxis ? 5 : 0, left: 0 }}
          syncId="unified-chart"
          syncMethod="value"
          onMouseMove={(state: any) => {
            const index = Number(state?.activePayload?.[0]?.payload?.__chartIndex);
            if (Number.isInteger(index)) onHover(index);
          }}
        >
          <CartesianGrid stroke="rgba(100, 116, 139, 0.16)" strokeDasharray="3 5" vertical={false} />
          <XAxis
            dataKey="__chartX"
            type="number"
            domain={[rows[0]?.__chartX ?? 0, rows.at(-1)?.__chartX ?? 1]}
            hide={!showXAxis}
            tickFormatter={(value) => formatX(spec, allRows.find((row) => row.__chartX === value), timeRange)}
            tick={{ fill: "#8190A5", fontSize: 10 }}
            axisLine={{ stroke: "#27344A" }}
            tickLine={false}
            minTickGap={28}
          />
          {axes.map((axis) => (
            <YAxis key={axis.id} yAxisId={axis.id} orientation={axis.side} scale={scaleMode === "log" || axis.scale === "log" ? "log" : "auto"} domain={[axis.domain?.min ?? (axis.domain?.includeZero ? 0 : "auto"), axis.domain?.max ?? "auto"]} tickFormatter={(value) => formatNumber(value, axis.format)} tick={{ fill: "#8190A5", fontSize: 10 }} axisLine={false} tickLine={false} width={56} mirror />
          ))}
          <Tooltip content={() => null} cursor={{ stroke: "#94A3B8", strokeWidth: 1 }} />
          {windows.map((window) => <ReferenceArea key={window.id} className="chart-event-window" x1={xByValue.get(window.xStart)} x2={xByValue.get(window.xEnd)} yAxisId={pane.axisIds[0]} fill={window.style?.fill || "#64748B"} fillOpacity={window.style?.opacity ?? 0.14} ifOverflow="hidden" />)}
          {bands.map((band) => <ReferenceArea key={band.id} className="chart-reference-band" yAxisId={band.axisId} y1={band.yStart} y2={band.yEnd} fill={band.style?.fill || "#1D4ED8"} fillOpacity={band.style?.opacity ?? 0.12} ifOverflow="extendDomain" />)}
          {series.map((item, index) => renderSeries(item, index, rows))}
          {references.map((line) => {
            const segment: readonly [{ x?: number; y: number }, { x?: number; y: number }] | undefined = line.xStart !== undefined && line.xEnd !== undefined ? [{ x: xByValue.get(line.xStart), y: line.value }, { x: xByValue.get(line.xEnd), y: line.value }] : undefined;
            const labelPeers = references.filter((candidate) =>
              (candidate.labelPosition || "end") === (line.labelPosition || "end") &&
              candidate.xStart === line.xStart && candidate.xEnd === line.xEnd
            );
            const peerIndex = labelPeers.findIndex((candidate) => candidate.id === line.id);
            const offset = (peerIndex - (labelPeers.length - 1) / 2) * 14;
            return <ReferenceLine key={line.id} yAxisId={line.axisId} {...(segment ? { segment } : { y: line.value })} stroke={line.style?.color || "#94A3B8"} strokeWidth={line.style?.lineWidth || 1} strokeOpacity={line.style?.opacity ?? 1} strokeDasharray={dashArray(line.style?.dash || "dashed")} ifOverflow="extendDomain" label={{ value: line.label, content: (props: any) => <ReferenceLabel {...props} id={line.id} semanticElementId={line.semanticElementId} color={line.style?.color || "#94A3B8"} position={line.labelPosition || "end"} offset={offset} /> }} />;
          })}
          {markers.map((marker) => {
            const value = markerValue(spec, marker);
            const targetSeries = spec.series.find((candidate) => candidate.id === marker.seriesId);
            const x = xByValue.get(marker.x);
            if (value === null || !targetSeries || x === undefined) return null;
            return <ReferenceDot key={marker.id} x={x} y={value} yAxisId={targetSeries.axisId} ifOverflow="visible" shape={(props: any) => <MarkerLabel {...props} marker={marker} />} />;
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
